import uniq from 'utils/uniq';
import produce from 'utils/produce';
import { getSkuProductPrices } from './product';

/**
 * Transformer
 *
 * f(specifications, skuProducts, prismicData) = PRODUCTS
 * @param {object} specifications
 * @param {Array} skuProducts
 * @param {Array} productsData
 * @returns {Array<object>}
 */
export const productsTransformer = (specifications, skuProducts, productsData) => {
  const products = [];

  Object.entries(specifications).forEach(([id, specification]) => {
    const skus = uniq([...(specification.skus ?? []), ...(specification.bundles ?? [])]);

    const currentSkuProducts = skus
      .map(sku => skuProducts.find(p => p.id === sku))
      .filter(p => !!p);

    const upsellItems = (specification.upsell ?? [])
      .map(upsell => {
        const upsellSkuProducts = upsell.skus
          .map(sku => skuProducts.find(p => p.id === sku))
          .filter(p => !!p);

        if (upsellSkuProducts.length > 0) {
          return {
            ...upsell,
            skuProducts: upsellSkuProducts,
          };
        }
        return null;
      })
      .filter(p => !!p);

    /**
     * Get all possible prices of skuProducts (check bundles also)
     */
    const prices = [];
    currentSkuProducts.forEach(skuProduct => {
      prices.push(...getSkuProductPrices(skuProduct));
      skuProduct.products.primary.forEach(sku => {
        const primarySkuProduct = skuProducts.find(p => p.id === sku);
        if (primarySkuProduct) {
          prices.push(...getSkuProductPrices(primarySkuProduct));
        }
      });
    });

    let priceData = {};
    if (currentSkuProducts.length > 0) {
      /**
       * First skuProduct is used as source of details (images, currency, etc.)
       */
      const [skuProduct] = currentSkuProducts;

      priceData = {
        currency: skuProduct.currency,
      };
    }

    const productData = productsData.find(item => item.id === id) || {};

    products.push({
      id,
      url: specification.url,
      images: specification.images.map(image => image.key),
      options: currentSkuProducts.filter(p => !p.bundle),
      bundles: currentSkuProducts.filter(p => p.bundle),
      upsell: upsellItems,
      prices: uniq(prices).sort((a, b) => a - b),
      reducer: specification.reducer,
      name: productData.name || specification.name || 'N/A', // No data in Prismic.io
      shortDescription: productData.shortDescription || specification.description || 'N/A', // No data in Prismic.io
      ...priceData,
      config: specification.config ?? {},
    });
  });

  return products;
};

/**
 * Transformer
 *
 * f(specifications, skuProducts) = SKU_PRODUCTS
 * @param {object} specifications
 * @param {Array} skuProducts
 * @returns {Array<object>}
 */
export const skuProductsTransformer = (specifications, skuProducts) => {
  // TODO in some cases, it should be useful
  return skuProducts;
};

/**
 * Custom SkuProduct Transformer. Used to update skuProduct e.g. disable some color options.
 *
 * f(skuProduct, opts) = skuProduct
 * @param {object} skuProduct
 * @param {object} opts
 * @param {string} opts.type
 * @returns {object}
 */
export const customSkuProductTransformer = (skuProduct, opts = {}) => {
  switch (opts.type) {
    // it's not used anymore
    case 'BUNDLE_SECONDARY_PRODUCTS':
      // Family history poster bundle offers only OAK version of rails.
      if (opts.primaryId.startsWith('fhp-') && skuProduct.id.startsWith('wooden-rail-')) {
        if (skuProduct.templateMetaData?.options?.color) {
          return produce(skuProduct, draft => {
            // when product doesn't offer Oak color, then color is empty array
            draft.templateMetaData.options.color = skuProduct.templateMetaData.options.color.filter(
              color => color === 'Oak',
            );
          });
        }
      }
      break;

    default:
      break;
  }

  return skuProduct;
};
